import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
// Models
import { IPrompt } from 'app/store/AIChatPrompts/AIChatPrompts.models';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { deletePrompt } from 'app/store/AIChatPrompts/AIChatPrompts.async';
// Selectors
import { selectLoading } from 'app/store/AIChatPrompts/AIChatPrompts.selectors';
// Mui
import {
  Theme, Box, Typography,
  ListItem, ListItemText
} from '@mui/material';
// Icon
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
// Components
import Labels from 'app/components/Labels';
import { IconButton } from 'app/components/Mui';
// Dialogs
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
import AIPromptFormDialog from 'app/dialogs/AIPromptFormDialog';
// Hooks
import useMenu from 'app/hooks/useMenu';
import useToggle from 'app/hooks/useToggle';
import useAccountStatus from 'app/hooks/useAccountStatus';

type Props = {
  aiPrompt: IPrompt;
}

const AIPromptItem:FC<Props> = ({
  // Props
  aiPrompt
}) => {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const loading = useAppSelector(selectLoading);

  const { isSuspended } = useAccountStatus();
  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  const handleConfirm = () => {
    dispatch(deletePrompt(aiPrompt.id));
  }

  return (
    <Fragment>
      <ListItem
        sx={{
          '& + &': {
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }
        }}
        secondaryAction={
          <Fragment>
            <IconButton
              name={`Prompt dropdown ${aiPrompt.id}`}
              aria-label="Prompt item dropdown button"
              onClick={openMenu}
            ><MoreVertIcon /></IconButton>
            <Menu>
              <MenuItem
                name={`Edit prompt ${aiPrompt.id}`}
                onClick={toggle}
                disabled={isSuspended}
              >{t('buttons.edit')}</MenuItem>
              <MenuItem
                name={`Delete prompt ${aiPrompt.id}`}
                sx={{
                  color: (theme:Theme) => theme.palette.error.main
                }}
                onClick={toggleConfirmationDialog}
              >{t('buttons.delete')}</MenuItem>
            </Menu>
          </Fragment>
        }
      >
        <ListItemText primary={
          <Fragment>
            <Typography
              display="block"
              variant="subtitle2"
              component="span"
            >{aiPrompt.title}</Typography>
            {aiPrompt.labels ? (
              <Box sx={{ pt: 2 }}>
                <Labels id={`prompt item labels ${aiPrompt.id}`} labels={aiPrompt.labels} />
              </Box>
            ) : null}
          </Fragment>
        } />
      </ListItem>
      {open ? (
        <AIPromptFormDialog
          open={open}
          onClose={toggle}
          aiPrompt={aiPrompt}
        />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          loading={loading}
          title={t('dialogs.confirmation.deleteTitle', { dataType: t('labels.prompt') })}
          content={(
            <Trans
              t={t} i18nKey="dialogs.confirmation.deleteConfirmationWithName"
              components={{ strong: <strong /> }}
              values={{ dataType: t('labels.prompt'), dataName: aiPrompt.title }}
            />
          )}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Fragment>
  )
}

export default AIPromptItem
